import { Fragment, useCallback, useRef } from "react";

import clsx from "clsx";

import { Popover, PopoverPanel, Transition } from "@headlessui/react";
import {
  type constants,
  type DateRangeChoiceV3,
  type FixedDateInterval,
  type ITimeGranularityV2,
  dateRangeOptionsForTimeGranularity,
  DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY,
} from "@north-beam/nb-common";
import {
  DateRangeDisplay,
  DateRangePicker,
  formatDateRangeText,
} from "@shared/date-range-picker";
import { TimeGranularityV2Select } from "@shared/selects/time-granularity-v2-select";

import { AttributionButton } from "../minimized-top-bar/components/attribution-button";
import { CompareDateRangeMenu } from "../top-level-filters/components/compare-date-range-menu";
import { DATE_RANGE_CHOICE_LABELS } from "../top-level-filters/components/date-filters";
import { maxLookbackDaysForTimeGranularity } from "../utils/max-lookback-days-for-time-granularity";

export type DateRangeSelectValues = {
  timeGranularity: ITimeGranularityV2;
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  compareDateRange: constants.CompareDateRange | null;
};

export interface DateRangeSelectProps {
  // TODO: Replace each initial value prop with a 'initialValue' prop of type DateRangeSelectValues.
  readonly timeGranularity: ITimeGranularityV2;
  readonly dateRange: FixedDateInterval | DateRangeChoiceV3;
  readonly compareDateRange: constants.CompareDateRange | null;
  readonly hideHourlyOption?: boolean;
  readonly onChange?: (params: DateRangeSelectValues) => void;
  /* These individual onChange handlers are only here to preserve existing behavior - they
     should eventually be removed in favor of a single 'onChange' handler. */
  readonly onTimeGranularityUpdate?: (value: ITimeGranularityV2) => void;
  readonly onDateRangeUpdate?: (
    value: FixedDateInterval | DateRangeChoiceV3,
  ) => void;
  readonly onCompareDateRangeUpdate?: (
    value: constants.CompareDateRange | null,
  ) => void;
}

export const DateRangeSelect = ({
  timeGranularity,
  dateRange,
  compareDateRange,
  hideHourlyOption = false,
  onChange: _onChange,
  onTimeGranularityUpdate,
  onDateRangeUpdate,
  onCompareDateRangeUpdate,
}: DateRangeSelectProps) => {
  const previousValues = useRef<DateRangeSelectValues>({
    timeGranularity,
    dateRange,
    compareDateRange,
  });
  const [maxLookbackDays] = maxLookbackDaysForTimeGranularity(timeGranularity);

  const dateRangeCurrent =
    typeof dateRange === "string"
      ? { label: DATE_RANGE_CHOICE_LABELS[dateRange], value: dateRange }
      : dateRange;

  const onChange = useCallback(
    (values: Partial<DateRangeSelectValues>) => {
      /* These individual onChange handlers are only here to preserve existing behavior - they
         should eventually be removed in favor of a single 'onChange' handler. */
      if (values.timeGranularity !== undefined) {
        onTimeGranularityUpdate?.(values.timeGranularity);
      }
      if (values.dateRange !== undefined) {
        onDateRangeUpdate?.(values.dateRange);
      }
      if (values.compareDateRange !== undefined) {
        onCompareDateRangeUpdate?.(values.compareDateRange);
      }
      const updatedState: DateRangeSelectValues = {
        ...previousValues.current,
        ...values,
      };
      _onChange?.(updatedState);
      previousValues.current = updatedState;
    },
    [
      onTimeGranularityUpdate,
      onDateRangeUpdate,
      onCompareDateRangeUpdate,
      _onChange,
    ],
  );

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <AttributionButton
            variant="popover"
            open={open}
            label={
              <i
                className={clsx("fa-regular fa-sharp fa-calendar text-sm", {
                  "text-primary": !open,
                  "text-white": open,
                })}
              />
            }
            displayValue={formatDateRangeText(dateRangeCurrent)}
          />
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <PopoverPanel
              className={clsx(
                "absolute z-100 mt-2 w-72 elevation-1 rounded-md p-3 bg-white",
                "flex flex-col space-y-2",
              )}
            >
              <TimeGranularityV2Select
                timeGranularity={timeGranularity}
                hideHourlyOption={hideHourlyOption}
                onChange={(value: ITimeGranularityV2) =>
                  onChange({
                    dateRange:
                      DEFAULT_DATE_RANGE_CHOICE_FOR_TIME_GRANULARITY[value],
                    timeGranularity: value,
                  })
                }
              />
              <DateSelect
                maxLookbackDays={maxLookbackDays}
                isLoading={false}
                dateRange={dateRange}
                timeGranularity={timeGranularity}
                onUpdate={(value) => {
                  onChange({
                    dateRange: value,
                  });
                  setTimeout(() => close(), 0);
                }}
              />
              <div className="nb-input-container">
                <label className="label">COMPARE TO</label>
                <CompareDateRangeMenu
                  className="w-full"
                  timeGranularity={timeGranularity}
                  compareDateRange={compareDateRange}
                  onCompareDateRangeUpdate={(value) =>
                    onChange({ compareDateRange: value })
                  }
                />
              </div>
            </PopoverPanel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

interface DateSelectProps {
  isLoading: boolean;
  dateRange: FixedDateInterval | DateRangeChoiceV3;
  timeGranularity: ITimeGranularityV2;
  onUpdate: (opt: any) => void;
  maxLookbackDays?: number;
  showDateRangeDisplay?: boolean;
  className?: string;
}

function DateSelect({
  isLoading,
  dateRange,
  timeGranularity,
  onUpdate,
  maxLookbackDays,
  showDateRangeDisplay = true,
  className,
}: DateSelectProps) {
  return (
    <div className={className}>
      <DateRangePicker<{
        label: string;
        value: DateRangeChoiceV3;
      }>
        label="DATE RANGE"
        disabled={isLoading}
        nonCustomChoices={dateRangeOptionsForTimeGranularity(
          timeGranularity,
        ).map(({ value }) => ({
          label: DATE_RANGE_CHOICE_LABELS[value],
          value,
        }))}
        dateRange={dateRange}
        onUpdate={onUpdate}
        maxLookbackDays={maxLookbackDays}
      />
      {showDateRangeDisplay && (
        <div className="mt-0.5 text-xs font-light">
          <DateRangeDisplay
            dateRange={dateRange}
            onConversionToFixed={onUpdate}
          />
        </div>
      )}
    </div>
  );
}
